import { useState } from 'react';
import { Lock, Users } from 'lucide-react';
import type { IntegranteDto, ObservacionTitular } from '@lib/services/portal.service';
import { isValidEmail } from '@lib/validators';
import { capitalizeFullName } from '@lib/utils/text';
import { ReportarCampo } from './ReportarCampo';

/**
 * Paso 2 del wizard — Grupo familiar.
 *
 * Solo muestra a los familiares (el titular ya validó sus propios datos en
 * el paso anterior). Si el titular no tiene familiares cargados, se muestra
 * un mensaje aclarándolo en vez de una lista vacía.
 *
 * Política: el titular NO puede agregar ni eliminar familiares. Solo puede
 * editar los datos de contacto y dirección de cada uno. Si detecta que un
 * familiar no debería estar en el grupo, o que falta alguien, lo reporta
 * como observación (ver `ReportarCampo`) y RRHH/Medicus corrige desde su
 * lado.
 */

interface Props {
  token: string;
  familiares: IntegranteDto[];
  observaciones: ObservacionTitular[];
  onBack: () => void;
  onContinue: () => void;
  onUpdate: (id: string, patch: Partial<IntegranteDto>) => void;
  busy: boolean;
}

// Catálogo reducido por requerimiento de producto: titular, cónyuge, hijos
// y adherentes (otros adultos que el titular decida sumar al plan).
const PARENTESCO_LABEL: Record<string, string> = {
  '0': 'Titular',
  '5': 'Cónyuge',
  '7': 'Hijo/a',
  '13': 'Adherente',
};

// Opciones para el <select> del popover "Reportar como incorrecto".
// Sin "Adherente": desde el 02/10/2026 no se dan de alta adherentes, así que
// tampoco se puede proponer ese parentesco como corrección.
const OPC_PARENTESCO = [
  { value: '5', label: 'Cónyuge' },
  { value: '7', label: 'Hijo/a' },
];

export function FamiliaStep({
  token,
  familiares,
  observaciones,
  onBack,
  onContinue,
  onUpdate,
  busy,
}: Props) {
  const sinFamiliares = familiares.length === 0;

  return (
    <div className="onboarding__form">
      {sinFamiliares ? (
        <aside
          style={{
            padding: '14px 16px',
            marginBottom: 16,
            background: '#F9FAFB',
            border: '1px solid var(--color-outline)',
            color: 'var(--color-element-mid)',
            borderRadius: 10,
            fontSize: 14,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <Users size={18} style={{ marginTop: 2, opacity: 0.6 }} />
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>
              No tenés grupo familiar cargado.
            </strong>
            Tu organización no registró familiares asociados a tu legajo. Si esto es
            un error, comunicate con el área de RRHH de tu organización para que
            actualicen la información.
          </div>
        </aside>
      ) : (
        <>
          <aside
            style={{
              padding: '10px 12px',
              marginBottom: 16,
              background: '#EFF6FF',
              border: '1px solid #BFDBFE',
              color: '#1E40AF',
              borderRadius: 10,
              fontSize: 13,
            }}
          >
            Algunos de los datos de tu grupo familiar ya están cargados. Si tenés
            que corregirlos, eliminar o agregar a alguien, usá el botón{' '}
            <strong>Reportar como incorrecto</strong>. Solo podrás editar los campos
            de Contacto y Dirección.
          </aside>

          <h3 className="heading-5" style={{ marginBottom: 12 }}>
            Tu grupo familiar ({familiares.length}{' '}
            {familiares.length === 1 ? 'integrante' : 'integrantes'})
          </h3>

          {familiares.map((f) => (
            <IntegranteCard
              key={f.id}
              token={token}
              integrante={f}
              observaciones={observaciones}
              onUpdate={(patch) => onUpdate(f.id, patch)}
              busy={busy}
            />
          ))}
        </>
      )}

      <div className="onboarding__actions" style={{ marginTop: 16 }}>
        <button className="btn btn-ghost" onClick={onBack} disabled={busy}>
          Volver
        </button>
        <button className="btn btn-primary" onClick={onContinue} disabled={busy}>
          <span className="btn-font-style">Continuar</span>
        </button>
      </div>
    </div>
  );
}

function IntegranteCard({
  token,
  integrante,
  observaciones,
  onUpdate,
  busy,
}: {
  token: string;
  integrante: IntegranteDto;
  observaciones: ObservacionTitular[];
  onUpdate: (patch: Partial<IntegranteDto>) => void;
  busy: boolean;
}) {
  const parentesco =
    PARENTESCO_LABEL[integrante.parentesco ?? ''] ?? integrante.parentesco ?? '—';
  const yaReportado = (campo: string) =>
    observaciones.some((o) => o.asociadoId === integrante.id && o.campo === campo);

  const [domicilio, setDomicilio] = useState(integrante.domicilio ?? '');
  const [numero, setNumero] = useState(integrante.numero ?? '');
  const [piso, setPiso] = useState(integrante.piso ?? '');
  const [depto, setDepto] = useState(integrante.depto ?? '');
  const [cp, setCp] = useState(integrante.cp ?? '');
  const [areaCelular, setAreaCelular] = useState(integrante.areaCelular ?? '');
  const [numeroCelular, setNumeroCelular] = useState(integrante.numeroCelular ?? '');
  const [email, setEmail] = useState(integrante.email ?? '');
  const [savedFlash, setSavedFlash] = useState(false);
  const [errUI, setErrUI] = useState<string | null>(null);

  function guardar() {
    if (email && !isValidEmail(email)) {
      setErrUI('Email inválido');
      return;
    }
    if (cp && (!/^\d{4}$/.test(cp) || /^0+$/.test(cp))) {
      setErrUI('CP inválido (4 dígitos, no puede ser 0)');
      return;
    }
    if (areaCelular && !/^\d{2,4}$/.test(areaCelular)) {
      setErrUI('Característica: 2 a 4 dígitos sin 0');
      return;
    }
    if (numeroCelular && !/^\d{6,8}$/.test(numeroCelular)) {
      setErrUI('Celular: 6 a 8 dígitos');
      return;
    }
    setErrUI(null);
    onUpdate({
      domicilio: domicilio.trim(),
      numero: numero.trim(),
      piso: piso.trim(),
      depto: depto.trim(),
      cp,
      areaCelular,
      numeroCelular,
      email: email.trim().toLowerCase(),
    });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  }

  return (
    <section
      className="card"
      style={{
        padding: 14,
        marginTop: 12,
        border: '1px solid var(--color-outline)',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: '1px solid var(--color-outline)',
        }}
      >
        <Users size={18} style={{ color: 'var(--color-primary-darker)' }} />
        <strong style={{ fontSize: 14 }}>
          {capitalizeFullName(integrante.nombres, integrante.apellSolt)}
        </strong>
        <span
          style={{
            fontSize: 11,
            padding: '2px 8px',
            borderRadius: 12,
            background: 'rgba(0,37,100,0.08)',
            color: 'var(--color-primary-darker)',
            fontWeight: 600,
          }}
        >
          {parentesco}
        </span>
      </header>

      {/* Datos no editables */}
      <h4 style={{ fontSize: 12, color: 'var(--color-element-mid)', margin: '6px 0 4px' }}>
        Datos no editables
      </h4>
      <div className="onboarding__rowGrid" style={{ marginBottom: 4 }}>
        <Locked
          label="DNI"
          value={integrante.documento ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={integrante.id}
              campo="documento"
              labelCampo="DNI"
              valorActual={integrante.documento}
              yaReportado={yaReportado('documento')}
              tipo="text"
            />
          }
        />
        <Locked
          label="Fecha de nacimiento"
          value={integrante.fechaNacimiento ?? '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={integrante.id}
              campo="fechaNacimiento"
              labelCampo="Fecha de nacimiento"
              valorActual={integrante.fechaNacimiento}
              yaReportado={yaReportado('fechaNacimiento')}
              tipo="date"
            />
          }
        />
        <Locked
          label="Nombre completo"
          value={capitalizeFullName(integrante.nombres, integrante.apellSolt) || '—'}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={integrante.id}
              campo="nombreCompleto"
              labelCampo="Nombre completo"
              valorActual={capitalizeFullName(integrante.nombres, integrante.apellSolt)}
              yaReportado={yaReportado('nombreCompleto')}
              tipo="text"
            />
          }
        />
        <Locked
          label="Parentesco"
          value={parentesco}
          reportar={
            <ReportarCampo
              token={token}
              asociadoId={integrante.id}
              campo="parentesco"
              labelCampo="Parentesco"
              valorActual={parentesco}
              yaReportado={yaReportado('parentesco')}
              tipo="enum"
              opciones={OPC_PARENTESCO}
            />
          }
        />
      </div>

      {/* Datos editables: contacto + dirección */}
      <h4 style={{ fontSize: 12, color: 'var(--color-element-mid)', margin: '14px 0 4px' }}>
        Datos de contacto y dirección
      </h4>
      <p style={{ fontSize: 12, color: 'var(--color-element-mid)', margin: '0 0 8px' }}>
        Si convive con vos, podés repetir el mismo domicilio. De lo contrario
        indicá dónde vive actualmente.
      </p>
      <Field
        label="Calle"
        value={domicilio}
        onChange={setDomicilio}
        autoComplete="address-line1"
      />
      <div className="onboarding__rowGrid onboarding__rowGrid--pair">
        <Field
          label="Número"
          value={numero}
          onChange={(x) => setNumero(x.replace(/\D/g, ''))}
          inputMode="numeric"
        />
        <Field label="Piso" value={piso} onChange={setPiso} />
      </div>
      <div className="onboarding__rowGrid onboarding__rowGrid--pair">
        <Field label="Depto" value={depto} onChange={setDepto} />
        <Field
          label="CP"
          value={cp}
          onChange={(x) => setCp(x.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={4}
          hint="4 dígitos"
        />
      </div>
      <div className="onboarding__rowGrid onboarding__rowGrid--pair">
        <Field
          label="Caracteristica"
          value={areaCelular}
          onChange={(x) => setAreaCelular(x.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric"
          maxLength={4}
          hint="Sin 0"
        />
        <Field
          label="Celular"
          value={numeroCelular}
          onChange={(x) => setNumeroCelular(x.replace(/\D/g, '').slice(0, 8))}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          maxLength={8}
        />
      </div>
      <Field
        label="Email"
        type="email"
        value={email}
        onChange={(x) => setEmail(x.trim().toLowerCase())}
        inputMode="email"
        autoComplete="email"
      />

      {errUI && (
        <p className="status status-error" style={{ display: 'block', marginTop: 8 }}>
          {errUI}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={guardar}
          disabled={busy}
          style={{ fontSize: 13 }}
        >
          Guardar contacto y dirección
        </button>
        {savedFlash && (
          <span style={{ fontSize: 12, color: '#047857' }}>✓ Guardado</span>
        )}
      </div>
    </section>
  );
}

function Locked({
  label,
  value,
  reportar,
}: {
  label: string;
  value: string;
  reportar: React.ReactNode;
}) {
  return (
    <div
      className="onboarding__field onboarding__field--readonly"
      style={{ background: '#F9FAFB' }}
    >
      <span
        className="onboarding__fieldLabel"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <Lock size={10} style={{ opacity: 0.5 }} /> {label}
      </span>
      <span className="onboarding__fieldValue" style={{ fontWeight: 500 }}>
        {value}
      </span>
      <div style={{ marginTop: 4 }}>{reportar}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  hint,
  type,
  inputMode,
  autoComplete,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'url' | 'search' | 'decimal' | 'none';
  autoComplete?: string;
  maxLength?: number;
}) {
  return (
    <div className="onboarding__field">
      <label className="onboarding__fieldLabel">{label}</label>
      <input
        className="input"
        type={type ?? 'text'}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="onboarding__fieldHint">{hint}</span>}
    </div>
  );
}
